import {Component, Inject, OnDestroy, OnInit, ViewChild, ViewContainerRef} from "@angular/core";
import {Operador} from "./clases/Operador";

import {Condicion} from "./clases/Condicion";
import {EncuestaDTO} from "@DTOS";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ItemPreguntaModelDTO, PreguntaModelDTO} from "@Models";
import {Nilable} from "@shared/utils";
import {Subscription} from "rxjs";
import {take} from "rxjs/operators";
import {CondicionesToken, OperadoresToken} from "@shared/tokens";
import {TipoOperadorEnum} from "@shared/enums";


@Component({
    templateUrl: "./editor-condiciones.component.html",
    styleUrls: ["./editor-condiciones.component.scss"]
})
export class EditorCondicionesComponent implements OnInit, OnDestroy {
    @ViewChild("operando", { read: ViewContainerRef, static: true }) operandoRef: ViewContainerRef;

    public encuesta: EncuestaDTO;
    public pregunta: PreguntaModelDTO;
    public item: ItemPreguntaModelDTO;

    public operador: Nilable<Operador>;
    public condicion: Nilable<Condicion>;

    private subs = new Subscription();

    constructor(private activeModal: NgbActiveModal, @Inject(OperadoresToken) public operadores: Operador[],
        @Inject(CondicionesToken) public condiciones: Condicion[]) {}

    ngOnInit() {
        this.cargarCondicion();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    public cerrar() {
        this.activeModal.dismiss();
    }

    public agregarOperador(operador: Operador) {
        const clon = operador.clone();
        this.subs.add(
            clon.borrar$.pipe(take(1)).subscribe(() => {
                this.operador = null;
            })
        );
        this.operador = clon;
    }

    public agregarCondicion(condicion: Condicion) {
        const clon = condicion.clone();
        this.subs.add(
            clon.borrar$.pipe(take(1)).subscribe(() => {
                this.condicion = null;
            })
        );
        this.condicion = clon;
    }

    public guardarCondicion() {
        let condicionTexto = "";

        if (this.operador != null) {
            condicionTexto = this.operador.formateado();
        }

        if (this.condicion != null) {
            condicionTexto += this.condicion.formateado();
        }

        condicionTexto = condicionTexto.startsWith("(") ? condicionTexto.slice(1, condicionTexto.length - 1) : condicionTexto;
        condicionTexto = condicionTexto.endsWith("&") || condicionTexto.endsWith("|") || condicionTexto.endsWith("~") ?
            condicionTexto.substring(0, condicionTexto.length - 1) : condicionTexto;

        if (this.item) {
            this.item.Condiciones = condicionTexto;
        } else {
            this.pregunta.Condiciones = condicionTexto;
        }
        this.activeModal.close();
    }

    private cargarCondicion() {
        let condicion = this.item ? this.item.Condiciones : this.pregunta.Condiciones;
        if (!condicion) {
            return;
        }
        condicion = condicion.endsWith("&") || condicion.endsWith("|") || condicion.endsWith("~") ?
            condicion.substring(0, condicion.length - 1) : condicion;
        const operadores: string[] = [];
        let datos: string[] = [];
        let acumuladorPalabra = "";
        for (const caracter of condicion) {
            if (["&", "|", "~", "(", ")"].includes(caracter)) {
                if (acumuladorPalabra !== "") {
                    datos.push(acumuladorPalabra);
                    acumuladorPalabra = "";
                }
                if (["(", ")"].includes(caracter)) {
                    continue;
                }
                operadores.push(caracter);
            } else {
                acumuladorPalabra += caracter;
            }
        }
        datos.push(acumuladorPalabra);
        datos = datos.map(x => x.trim()).filter(x => x !== "");

        if (operadores.length === 0) {
            this.condicion = this.condicionAClase(datos[0]);
            this.subs.add(
                this.condicion.borrar$.pipe(take(1)).subscribe(() => {
                    this.condicion = null;
                })
            );
            return;
        }

        let padreOperador: Nilable<Operador>;
        let cambioOperador = false;
        operadores.forEach(operadorString => {
            if (cambioOperador) {
                const operadorNuevo = this.operadorAClase(operadorString);
                padreOperador!.operadores.push(operadorNuevo);
                padreOperador = operadorNuevo;

                padreOperador.condiciones.push(this.condicionAClase(datos.shift()!));
                if (padreOperador.tipoOperador === TipoOperadorEnum.BINARIO) {
                    padreOperador.condiciones.push(this.condicionAClase(datos.shift()!));
                }
                return;
            }

            if (padreOperador == null) {
                cambioOperador = false;
                padreOperador = this.operadorAClase(operadorString);

                if (this.operador == null) {
                    this.operador = padreOperador;
                    this.subs.add(
                        this.operador.borrar$.pipe(take(1)).subscribe(() => {
                            this.operador = null;
                        })
                    );
                }

                padreOperador.condiciones.push(this.condicionAClase(datos.shift()!));
                if (padreOperador.tipoOperador === TipoOperadorEnum.BINARIO) {
                    padreOperador.condiciones.push(this.condicionAClase(datos.shift()!));
                }
                return;
            }

            if (operadorString === "(" || operadorString === "~") {
                datos.push(padreOperador.condiciones[padreOperador.condiciones.length - 1].formateado());
                padreOperador.condiciones.pop();
            }

            if (operadorString === "(") {
                cambioOperador = true;
                return;
            }

            let condicionClase: Condicion;
            if (padreOperador.esElOperador(operadorString)) {
                condicionClase = this.crearCondicion(padreOperador, datos.shift()!);
                padreOperador.condiciones.push(condicionClase);
                return;
            }

            const operadorUnario = this.operadorAClase(operadorString);
            operadorUnario.borrar$.pipe(take(1)).subscribe(() => {
                if (padreOperador == null) {
                    return;
                }
                padreOperador.operadores = padreOperador.operadores.filter(operador => operador !== operadorUnario);
            });
            padreOperador.operadores.push(operadorUnario);
            condicionClase = this.crearCondicion(operadorUnario, datos.shift()!);
            operadorUnario.condiciones.push(condicionClase);
        });
    }

    private operadorAClase(operando: string): Operador {
        return this.operadores.find(operador => operador.esElOperador(operando))!.clone();
    }

    private condicionAClase(condicionString: string): Condicion {
        console.log("DEBUG ~ file: editor-condiciones.component.ts:196 ~ EditorCondicionesComponent ~ condicionAClase ~ condicionString:", condicionString);
        const clon = this.condiciones.find(condicion => condicion.esLaCondicion(condicionString))!.clone();
        clon.data = condicionString.trim();
        return clon;
    }

    private crearCondicion(operador: Operador, condicionString: string): Condicion {
        const condicion = this.condicionAClase(condicionString);
        condicion.borrar$.pipe(take(1)).subscribe(() => {
            if (operador == null) {
                return;
            }
            operador.condiciones = operador.condiciones.filter(condicionGuardada =>
                condicionGuardada !== condicion);
        });
        return condicion;
    }
}
